import { getAntdApp } from '@/utils/antdApp';

const DB_NAME = 'persisted_data';
const DB_VERSION = 1;
export const FILE_STORE_NAME = 'FILE_STORE_NAME';

interface FileDTO {
    key: string;
    path?: string;
    content: string;
}

class Database {
    private static instance: Database;
    private db: IDBDatabase | null = null;

    private constructor() { }

    public static getInstance(): Database {
        if (!Database.instance) {
            Database.instance = new Database();
        }
        return Database.instance;
    }

    private async openDatabase(): Promise<IDBDatabase> {
        if (this.db) {
            return this.db;
        }

        return new Promise((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, DB_VERSION);

            request.onupgradeneeded = (event) => {
                const db = (event.target as IDBOpenDBRequest).result;
                if (!db.objectStoreNames.contains(FILE_STORE_NAME)) {
                    const store = db.createObjectStore(FILE_STORE_NAME, { keyPath: 'key' });
                    store.createIndex('key', 'key', { unique: true });
                }
            };

            request.onsuccess = (event) => {
                this.db = (event.target as IDBOpenDBRequest).result;
                resolve(this.db);
            };

            request.onerror = (event) => {
                reject(`Database error: ${(event.target as IDBOpenDBRequest).error}`);
            };
        });
    }

    public async saveFileData(data: FileDTO): Promise<void> {
        const db = await this.openDatabase();
        const transaction = db.transaction(FILE_STORE_NAME, 'readwrite');
        const store = transaction.objectStore(FILE_STORE_NAME);
        console.log("save file data", data)
        store.add(data);
    }

    public async getFileData(key: string): Promise<FileDTO> {
        const db = await this.openDatabase();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(FILE_STORE_NAME, 'readonly');
            const store = transaction.objectStore(FILE_STORE_NAME);
            const index = store.index('key')
            const request = index.get(key);

            request.onsuccess = (event) => {
                resolve((event.target as IDBRequest).result);
            };

            request.onerror = (event) => {
                reject(`Get data error: ${(event.target as IDBRequest).error}`);
            };
        });
    }

    public async updateFileData(key: string, newData: FileDTO): Promise<boolean> {
        const db = await this.openDatabase();
        const transaction = db.transaction(FILE_STORE_NAME, 'readwrite');
        const store = transaction.objectStore(FILE_STORE_NAME);


        return new Promise((resolve, reject) => {
            // Use get to retrieve the full object instead of getKey
            const request = store.get(key);
    
            request.onsuccess = (event) => {
                console.log("update file data", newData);
                const existingData = (event.target as IDBRequest).result;
                
                if (existingData) {
                    // Update the existing data
                    const updatedData = { ...existingData, ...newData };
                    console.log("Updating with:", updatedData);
                    
                    // Put the updated data
                    const putRequest = store.put(updatedData);
                    
                    putRequest.onsuccess = () => {
                        console.log("Data successfully updated");
                        resolve(true);
                    };
                    
                    putRequest.onerror = (e) => {
                        console.error("Error putting updated data", e);
                        reject(`Put error: ${(e.target as IDBRequest).error}`);
                    };
                } else {
                    // If no existing data, just put the new data
                    console.log("No existing data, adding new:", newData);
                    const putRequest = store.put(newData);
                    
                    putRequest.onsuccess = () => {
                        console.log("New data successfully added");
                        resolve(true);
                    };
                    
                    putRequest.onerror = (e) => {
                        console.error("Error putting new data", e);
                        reject(`Put error: ${(e.target as IDBRequest).error}`);
                    };
                }
            };
            
            request.onerror = (event) => {
                console.error(`Update data error: ${(event.target as IDBRequest).error}`);
                reject(`Update data error: ${(event.target as IDBRequest).error}`);
            };
        });


    }

    public async deleteFileData(key: string): Promise<void> {
        const db = await this.openDatabase();
        const transaction = db.transaction(FILE_STORE_NAME, 'readwrite');
        const store = transaction.objectStore(FILE_STORE_NAME);
        const index = store.index('key');
        const request = index.getKey(key);

        return new Promise((resolve, reject) => {
            request.onsuccess = (event) => {
                const key = (event.target as IDBRequest).result;
                if (key) store.delete(key);
                resolve();
            };

            request.onerror = (event) => {
                console.error(`Delete data error: ${(event.target as IDBRequest).error}`);
                reject(`Delete data error: ${(event.target as IDBRequest).error}`);
            };
        })
    }


    public async destroyDatabase(): Promise<void> {
        if (!this.db) {
            return;
        }

        this.db.close();
        this.db = null;

        return new Promise((resolve, reject) => {
            const request = indexedDB.deleteDatabase(DB_NAME);

            request.onsuccess = () => {
                console.log('Database deleted successfully');
                resolve();
            };

            request.onerror = (event) => {
                console.error(`Delete database error: ${(event.target as IDBRequest).error}`);
                reject(`Delete database error: ${(event.target as IDBRequest).error}`);
            };

            request.onblocked = () => {
                console.warn('Delete database blocked');

                const errorMsg = 'Please close other tabs of this application and try again.';
                getAntdApp().message.error(errorMsg, 10);
                // reject(errorMsg);
            };
        });
    }
}

const dbInstance = Database.getInstance();

export { dbInstance as Database };